<template>
  <div id="style-editor" :class="pipClass">
    <b-card v-if="!hide">
      <template v-slot:header>
        <div style="position: relative; width: 100%">
          <h6 class="mb-0">Colors</h6>
        </div>
      </template>

      <b-tabs
        card
        size="sm"
        active-nav-item-class="font-weight-bold text-uppercase "
        content-class="mt-3"
        v-model="tab"
        id="tabs"
      >
        <colors-pip-menu />
        <b-tab title="Color palette">
          <b-card-text >
            <div v-for="color of palette" :key="color">
              <ColorPaletteSelector
                :initialColor="colorNameToHex(color)"
              />
            </div>
          </b-card-text>
        </b-tab>
        <b-tab title="Details">
          <b-card-text >
            <div v-for="element of styles" :key="element.name">
              <style-configurator :element="element" />
            </div>
          </b-card-text>
        </b-tab>
      </b-tabs>
      <b-button size="sm" class="mx-1 mb-1" @click.prevent="save">
        Save
      </b-button>
      <b-button size="sm" class="mx-1 mb-1" @click.prevent="cancelColorSetting">
        Cancel
      </b-button>
      <b-button size="sm" class="mx-1 mb-1" @click.prevent="factoryReset">
        Reset Defaults
      </b-button>
    </b-card>
  </div>
</template>
<script>
//---- components ---------
import ColorMixin from "./colors.js";
import ColorPaletteSelector from "./ColorPaletteSelector.vue";
import ColorsPipMenu from "./ColorsPipMenu.vue";
import StyleConfigurator from "./StyleConfigurator.vue";
//---- mixins -------------
//---- services -----------
import AdminService from "@/app/services/admin.service.js";
//---- 3rd-parties --------

export default {
  name: "Colors",
  components: { StyleConfigurator, ColorPaletteSelector, ColorsPipMenu },
  mixins: [ColorMixin],
  data() {
    return {
      forceRefresh: false,
      tab: 0,
      age: 0,
    };
  },
  props: { pipOnly: { type: Boolean, default: false } },
  created() {
    // save the initial values in vuex so we can see what changed
    // make a deep clone of this.styles to preserve the initial values
    // const initialValues = JSON.parse(JSON.stringify(this.styles));
    // for (let element of initialValues) {
    //   element.initialValues = [];
    //   for (let style of element.properties) {
    //     let value = this.getCssVar(element, style);
    //     element.initialValues.push({ style, value });
    //   }
    //   // we don't need properties anymore
    //   delete element.properties;
    // }
    // this.$store.dispatch("admin/setStyleFactorySettings", initialValues);
    this.$root.$on("update-color", this.updatePaletteColor);
    this.$root.$on("update-element-color", this.updateElementColor);
  },
  beforeDestroy() {
    this.$root.$off("update-color", this.updatePaletteColor);
    this.$root.$off("update-element-color", this.updateElementColor);
  },
  watch: {
    tab() {
      this.forceRefresh = true;
      this.$nextTick(() => (this.forceRefresh = false));
    },
  },
  methods: {
    // updates the css var and force the component to update
    updateElementColor(cssVar, value) {
        this.updateCssVar(cssVar, value);
        this.age++
        // console.log("Age "+this.age)
    },
    changePipStatus(pipStatus) {
      this.pipStatus = pipStatus;
    },
    factoryReset() {
      const initialValues = this.$store.getters["admin/styleFactorySettings"];
      this.applyStyles(initialValues);
      this.age++;
    },
    save() {
      // get the initial values to see what changed
      const initialValues = this.$store.getters["admin/styleFactorySettings"];
      const toSave = [];
      for (let element of this.styles) {
        let elementToSave = { name: element.name, values: [] };
        for (let style of element.properties) {
          let value = this.getCssVar(element.name, style);
          let defaultValue = this.getStyle(initialValues, element.name, style);
          if (value != defaultValue) {
            elementToSave.values.push({ style, value });
            if (this.debug)
              console.log("style was updated: "+element.name+'-'+style+': '+defaultValue+' -> '+value)
          }
        }
        if (elementToSave.values.length > 0)
          toSave.push(elementToSave);
      }
      AdminService.saveStyles(toSave);
    },
    getStyle(fromSaved, element, style) {
      if (!fromSaved)
        return undefined;
      const el = fromSaved.find((x) => (x.name == element))
      if (!el)
        return undefined;

      const sty = el.values.find((x) => (x.style == style))
      if (!sty)
        return undefined;

      return sty.value;
    },
    async cancelColorSetting() {
      // get the initial values to see what changed
      const factoryValues = this.$store.getters["admin/styleFactorySettings"];      
      const fromServer = await AdminService.loadStyles();
      this.applyStyles(factoryValues);  
      this.applyStyles(fromServer);
      this.age++;
      this.forceRefresh = true;
      this.$nextTick(() => (this.forceRefresh = false));
    },
    updatePaletteColor(fromColor, toColor) {
      if (this.debug)
        console.log("updatePaletteColor "+this.age);
      for (let element of this.styles) {
        for (let color of element.properties.filter((p) =>
          p.includes("color")
        )) {
          let value = this.getCssVar(element.name, color);
          if (value == fromColor) {
            if (this.debug)
              console.log(
                "updating " +
                  this.toCssVarName(element.name, color) +
                  " to " +
                  value
              );
            this.updateCssVar(this.toCssVarName(element.name, color), toColor);
          }
        }
      }
      this.age++;
      this.forceRefresh = true;
      this.$nextTick(() => (this.forceRefresh = false));
    },
    gatherPalette(criteria) {
      const palette = [];
      for (let element of this.styles) {
        for (let color of element.properties.filter((p) => {
          return (
            p.includes("color") &&
            (typeof criteria == "undefined" || color.includes(criteria))
          );
        })) {
          let value = this.colorNameToHex(this.getCssVar(element.name, color));
          if (this.debug)
            console.log(
              "palette, adding " + element.name + "-" + color + ": " + value
            );
          if (!palette.includes(value)) {
            palette.push(value);
          }
        }
      }
      palette.sort((a, b) => a > b);
      return palette;
    },
  },
  computed: {
    hide() {
      return this.pipOnly && this.pipStatus == "full-screen";
    },
    pipClass() {
      if (!this.pipOnly) return "full-screen";
      if (this.pipStatus == "full-screen") return "off-screen";
      return "pip " + this.pipStatus;
    },
    pipStatus() {
      return this.$store.getters["admin/stylePipStatusClass"];
    },
    palette() {
      this.age; // this reference is important! it's used to force re-calculation of the palette
      return this.gatherPalette();
    },
    paletteBg() {
      this.age; // this reference is important! it's used to force re-calculation of the palette
      return this.gatherPalette("-bg-");
    },
  },
};
</script>
<style scoped>
/* #style-editor.pip >>> .card-body {
  overflow: auto;
  height: 250px;
} */

#style-editor.pip >>> .card-body {
  padding: 3px;
}

#tabs >>> .card-body {
  overflow: auto;
  height: 250px;
}

.pip {
  position: fixed;
  z-index: 100000;
  width: 370px;
  height: 300px;
  box-shadow: 0 10px 16px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19) !important;
}

.top-right {
  top: 20px;
  right: 20px;
}

.bottom-right {
  bottom: 120px;
  right: 20px;
}

.top-left {
  top: 20px;
  left: 20px;
}

.bottom-left {
  bottom: 120px;
  left: 20px;
}
</style>